import { useState, useEffect } from 'react';
import { messaging } from '../lib/firebase/config';
import { getToken, onMessage } from 'firebase/messaging';
import { notificationService } from '../lib/notification-service';
import { errorLogger } from '../lib/error-logger';

export function useNotifications() {
  const [permission, setPermission] = useState('default');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Register Firebase messaging service worker
    if ('serviceWorker' in navigator && messaging) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Firebase SW registered:', registration);
        })
        .catch((error) => {
          console.error('Firebase SW registration failed:', error);
        });
    }
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (!messaging) {
      console.log('Firebase messaging not available');
      return false;
    }

    setLoading(true);
    
    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted') {
        const fcmToken = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FCM_VAPID_KEY
        });
        
        if (fcmToken) {
          setToken(fcmToken);
          
          // Save token using the notification service
          await notificationService.saveToken(fcmToken);
          
          console.log('FCM token saved:', fcmToken);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      errorLogger.logError('Notification Permission Error', {
        message: error.message,
        stack: error.stack,
        vapidKey: !!import.meta.env.VITE_FCM_VAPID_KEY,
        messaging: !!messaging,
        context: 'requestPermission'
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const setupForegroundListener = (callback) => {
    if (!messaging) return;
    
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      callback(payload);
      
      if (permission === 'granted') {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/icons/icon-192x192.png',
          tag: payload.data?.type || 'papersedge'
        });
      }
    });
  };

  return {
    permission,
    token,
    loading,
    requestPermission,
    setupForegroundListener,
    isSupported: 'Notification' in window && !!messaging
  };
}